import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { format_vote_score_label, vote_max_score, vote_step } from '../../services/voteStats';

import './VoteStars.scss';

/**
 * @returns {('full'|'half'|'empty')[]}
 */
export function vote_star_states(score, max_score, step) {
    const max = vote_max_score({ max_score });
    const st = vote_step({ step });
    const value = Number(score);
    if (!Number.isFinite(value))
        return Array.from({ length: max }, () => 'empty');

    const states = [];
    for (let slot = 1; slot <= max; slot += 1) {
        if (value >= slot)
            states.push('full');
        else if (st < 1 && value >= slot - st)
            states.push('half');
        else
            states.push('empty');
    }
    return states;
}

function VoteStar({ state }) {
    if (state === 'half') {
        return (
            <span className="VoteStars__star VoteStars__star--half" aria-hidden="true">
                <i className="fa fa-star-o VoteStars__star-outline" />
                <span className="VoteStars__star-half-fill">
                    <i className="fa fa-star VoteStars__star-filled" />
                </span>
            </span>
        );
    }

    return (
        <i
            className={
                state === 'full'
                    ? 'fa fa-star VoteStars__star VoteStars__star--full'
                    : 'fa fa-star-o VoteStars__star VoteStars__star--empty'
            }
            aria-hidden="true"
        />
    );
}

const VoteStars = ({
    score,
    maxScore,
    step,
    language,
    title,
    className = '',
    size = 'md',
}) => {
    const states = useMemo(
        () => vote_star_states(score, maxScore, step),
        [score, maxScore, step],
    );

    const tooltip = useMemo(() => {
        if (title)
            return title;
        if (!Number.isFinite(Number(score)))
            return undefined;
        return format_vote_score_label(score, step, language);
    }, [title, score, step, language]);

    return (
        <span
            className={`VoteStars VoteStars--${size} ${className}`.trim()}
            title={tooltip}
        >
            {states.map((state, index) => (
                <VoteStar key={index} state={state} />
            ))}
        </span>
    );
};

VoteStars.propTypes = {
    score: PropTypes.number,
    maxScore: PropTypes.number,
    step: PropTypes.number,
    language: PropTypes.string,
    title: PropTypes.string,
    className: PropTypes.string,
    size: PropTypes.oneOf(['sm', 'md']),
};

export default VoteStars;
